import * as React from 'react';
import {
  Type,
  Types,
  TypeImplementation,
  substituteType,
  getSubstitution,
  TypeCheckerState,
  Result,
  getMeta,
  VariableMap,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  EList,
  ELiteral,
  ESelect,
  EUnion,
  EVariable,
  TList,
  TTypeApplication,
  TTypeConstructor,
  TTypeReference,
  Expression,
} from '@packages/systems/wfdl-ast';
import {
  getBindingLabel,
  getBindingType,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import {pluginName} from '../constants';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  annotate,
  ContextualExpressions,
  getAnn,
  stripAnn,
  EHole as CEHole,
  stripAll,
  type ContextualExpression,
} from '@packages/utilities/contextual-expression';
import {
  DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';
import {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import useDebouncedValue from '@packages/systems/ui/hooks/useDebouncedValue';
import {getId} from '@packages/systems/core/utils/RecordUtils';
import {
  Autocomplete,
  HStack,
  Text,
  Button,
  FilterOptionsType,
  isOptionGroup,
  OptionType,
  OptionGroupType,
  AutocompleteOption,
} from '@packages/systems/spring';
import {
  useCollectionItemsPage,
  useReferencedCollectionItems,
} from '@packages/systems/cms/client';
import {findAvailableBindings} from '@packages/systems/wfdl-findBindings';
import {usePlugins} from '@packages/utilities/platform';
import {reduce} from '@packages/utilities/lazy-list';
import {getFirst, getSecond} from '@packages/utilities/Pair';
import {DYNAMO_VARIABLE} from '@packages/systems/dynamo/wfdl';
import {TDynamoRefValue} from '@packages/systems/plugins-utils/DynamoGateway';
import {TOption} from '@packages/systems/plugins/Option';
import {emptyArray} from '@packages/utilities/fp/utils';
import {getString} from '@packages/utilities/json';
import {
  useLocalizationSelector,
  selectPrimaryLocale,
} from '@packages/systems/localization';
import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';

export const TRefAnyOfConditionValue = (arg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(
      qualify(pluginName, typeName('RefAnyOfConditionValue'))
    ),
    arg,
  });

export type RefConditionValue =
  | {tag: 'Some'; val: unknown}
  | {tag: 'None'; val: Record<string, never>};

export const RefValueType = TypeImplementation.Custom({
  params: ['refCollectionId'],
  render: () => null,
  parseValue: (json) => {
    const str = getString(json);

    if (str === undefined) {
      return Result.Error('Expected a string for RefValue.');
    }

    return Result.Ok(str);
  },
});

export const RefConditionValueType = TypeImplementation.Alias({
  params: ['refCollectionId'],
  type: TOption(TDynamoRefValue(TTypeReference('refCollectionId'))),
  render: function RenderRefValue() {
    return null;
  },
});

/**
 * Type used to represent multiple CMS item references used for contains and
 * does not contain conditions both for ItemRef and ItemRefSet field predicates.
 */
export const RefAnyOfConditionValueType = TypeImplementation.Alias({
  params: ['refCollectionId'],
  type: TList(
    TTypeApplication({
      con: TTypeConstructor(qualify(pluginName, typeName('RefConditionValue'))),
      arg: TTypeReference('refCollectionId'),
    })
  ),
  /**
   * Autocomplete that renders a list of CMS items to select from.
   * `[(Post A | x), (Post B | x) |v]`
   */
  render: function RenderRefAnyOfConditionValue({expr, dispatch}) {
    const plugins = usePlugins();
    const [searchQuery, setSearchQuery] = React.useState('');

    const {
      checkerState,
      primaryLocale,
      type,
      referencedCollection,
      selectedOptions,
      selectedItems,
    } = useRefAnyOfConditionData(expr);

    const referencedCollectionId = referencedCollection?._id;

    const dynContextBindingOptions: AutocompleteOption[] = React.useMemo(() => {
      const itemType = type ? Types.getListItemType(type) : null;
      const typedHole = itemType
        ? annotate(CEHole(), {
            type: itemType,
            env: checkerState.env,
            errors: [],
          })
        : null;

      if (!typedHole) {
        return [];
      }

      const availableBindings = findAvailableBindings(
        typedHole,
        checkerState,
        plugins
      );

      return reduce(
        availableBindings.local,
        (acc: AutocompleteOption[], entry) => {
          const id = getFirst(entry);
          if (id !== 'Dynamo') {
            return acc;
          }
          const binding = getSecond(entry);
          for (const [bindingProp, childBinding] of binding.children) {
            if (bindingProp.startsWith('DYN_CONTEXT') && childBinding.expr) {
              acc.push({
                label: getBindingLabel(
                  checkerState.env.context.values,
                  childBinding.expr
                ),
                value: bindingProp,
                multiValueColor: 'purple',
              });
            }
          }
          return acc;
        },
        []
      );
    }, [type, checkerState, plugins]);

    // Query for the CMS items to show in the searchable list
    const {data: searchableListQueryResponse, isLoading} =
      useCollectionItemsPage(referencedCollection, {
        format: 'itemRef',
        query: useDebouncedValue({value: searchQuery, wait: 150}),
        locales: primaryLocale ? [primaryLocale] : undefined,
      });

    // Cache the labels for the selected items
    const itemLabelById = React.useMemo(
      () => new Map<string, string>(),
      [referencedCollectionId]
    );

    React.useEffect(() => {
      if (selectedItems) {
        selectedItems.forEach((item) => {
          if (!item) {
            return;
          }

          const label = getCmsItemLabel(item);
          const itemId = getId(item);

          itemLabelById.set(itemId, label);
        });
      }
    }, [selectedItems, itemLabelById]);

    const searchableListOptions = React.useMemo(() => {
      const cmsItemOptions = searchableListQueryResponse.items.map((item) => {
        const label = getCmsItemLabel(item);
        const itemId = getId(item);

        const cachedLabel = itemLabelById.get(itemId);

        if (!cachedLabel || cachedLabel !== label) {
          itemLabelById.set(itemId, label);
        }

        return {
          label,
          value: itemId,
        };
      });
      return [
        {
          label: 'Parent context fields',
          options: dynContextBindingOptions,
          shouldHideGroupLabel: true,
        },
        {
          label: 'CMS items',
          options: cmsItemOptions,
          shouldHideGroupLabel: true,
        },
      ];
    }, [
      dynContextBindingOptions,
      searchableListQueryResponse.items,
      itemLabelById,
    ]);

    // This ensures that the selected items always have corresponding tags
    // in the autocomplete input even when they are not included among the
    // CMS item options for the searchable list.
    const getSelectedOptionByValue = React.useCallback(
      (value: string) => {
        const label = itemLabelById.get(value);
        return {
          label: label ?? 'Unknown',
          value,
        };
      },
      [itemLabelById]
    );

    const onSelectedItemsChange = React.useCallback(
      (nextSelectedOptions: string[]) => {
        dispatch(
          updateExpressionAction(
            EList(
              nextSelectedOptions.map((option) =>
                option.startsWith('DYN_CONTEXT')
                  ? ESelect({
                      from: EVariable(DYNAMO_VARIABLE),
                      prop: option,
                    })
                  : EUnion({
                      tag: 'Some',
                      value: ELiteral({
                        name: qualify(pluginName, typeName('RefValue')),
                        value: option,
                      }),
                    })
              )
            )
          )
        );
      },
      [dispatch]
    );

    const clearSelectedItems = React.useCallback(() => {
      dispatch(updateExpressionAction(EList([])));
    }, [dispatch]);

    const renderOptionLabel = React.useCallback(
      (option: OptionType, meta: {context?: string}) => {
        const {label, value} = option;
        if (meta.context === 'menu') {
          if (value === 'DYN_CONTEXT') {
            // Highlight "Item" in "Parent Item"
            const {parentName} = getDynContextBindingFieldAndParentName(
              value,
              checkerState.env.context.values
            );
            if (parentName) {
              return (
                <>
                  Parent <Text colorVariant="purple">{parentName}</Text>
                </>
              );
            }
          } else if (value.startsWith('DYN_CONTEXT.')) {
            // Highlight "Item" in "Field of parent Item"
            const {fieldName, parentName} =
              getDynContextBindingFieldAndParentName(
                value,
                checkerState.env.context.values
              );
            if (fieldName && parentName) {
              return (
                <>
                  {fieldName} of parent{' '}
                  <Text colorVariant="purple">{parentName}</Text>
                </>
              );
            }
          }
        }
        return label;
      },
      [checkerState.env.context.values]
    );

    return (
      <Autocomplete
        data-automation-id="cms-item-any-of-input"
        onInputChange={setSearchQuery}
        onChange={onSelectedItemsChange}
        options={searchableListOptions}
        value={selectedOptions}
        selectionMode="multiple"
        checkmarkStyle="checkbox"
        filterOptions={filterDynContextOptions}
        getSelectedOptionByValue={getSelectedOptionByValue}
        isLoading={isLoading}
        isClearable={false}
        shouldCloseOnSelect={false}
        note={
          <AutocompleteListBoxFooter
            numSelectedItems={selectedOptions.length}
            clearSelectedItems={clearSelectedItems}
          />
        }
        hasGroupDividers
        renderOptionLabel={renderOptionLabel}
      />
    );
  },
  renderSummary: function RefAnyOfConditionValueSummaryRender({expr}) {
    const {
      checkerState,
      selectedItems,
      parsedSelectedOptions,
      isLoadingSelectedItems,
    } = useRefAnyOfConditionData(expr);

    const itemCount = parsedSelectedOptions.length;

    const labels = React.useMemo(() => {
      if (isLoadingSelectedItems) {
        return [];
      }
      return getRefAnyOfConditionLabels({
        parsedSelectedOptions,
        selectedItems,
        envTypes: checkerState.env.context.values,
      });
    }, [
      isLoadingSelectedItems,
      parsedSelectedOptions,
      selectedItems,
      checkerState,
    ]);

    if (isLoadingSelectedItems && itemCount > 0) {
      return <span>{itemCount} items</span>;
    }

    const firstLabel = labels[0];
    if (!firstLabel) {
      return <span>{itemCount} items</span>;
    }

    return (
      <>
        {firstLabel.type === 'binding' ? (
          <Text colorVariant="purple">{firstLabel.text}</Text>
        ) : (
          <span>{firstLabel.text}</span>
        )}
        {labels.length > 1 && <span> + {labels.length - 1} more</span>}
      </>
    );
  },
});

/**
 * Used both by the render and renderSummary to get the required data for
 * RefAnyOfConditionValue.
 */
function useRefAnyOfConditionData(expr: ContextualExpression) {
  const [checkerState] = React.useContext(TypeCheckerState);
  const collectionsState = useDesignerSelector(selectCollectionsState);
  const {collections} = collectionsState;
  const primaryLocale = useLocalizationSelector(selectPrimaryLocale);

  const {type: rawType} = getAnn(expr);
  const type = rawType
    ? substituteType(getSubstitution(checkerState))(rawType)
    : null;
  const referencedCollection = type
    ? getReferencedCollection(type, collections)
    : null;

  const {parsedSelectedOptions, selectedOptions} = React.useMemo(() => {
    const parsed = parseSelectedOptions(expr);
    return {
      parsedSelectedOptions: parsed,
      selectedOptions: parsed.map((option) => option.value),
    };
  }, [expr]);

  const selectedItemIds = React.useMemo(() => {
    return selectedOptions.filter(
      (option) => !option.startsWith('DYN_CONTEXT')
    );
  }, [selectedOptions]);

  const selectedItemsResponses = useReferencedCollectionItems(
    referencedCollection && selectedItemIds.length > 0
      ? {[referencedCollection._id]: selectedItemIds}
      : {},
    {
      format: 'itemRef',
      locales: primaryLocale ? [primaryLocale] : undefined,
    }
  );

  const selectedItems = React.useMemo(() => {
    return selectedItemsResponses.flatMap((resp) => resp.data?.items ?? []);
  }, [selectedItemsResponses]);

  const isLoadingSelectedItems = selectedItemsResponses.some(
    (resp) => resp.status === 'loading'
  );

  return {
    checkerState,
    primaryLocale,
    type,
    referencedCollection,
    selectedOptions,
    selectedItems,
    parsedSelectedOptions,
    isLoadingSelectedItems,
  };
}

export type ParsedSelectedOption =
  | {type: 'Id'; value: string}
  | {type: 'Bind'; value: string; bindingExpr: Expression};

function parseSelectedOptions(
  expr: ContextualExpression
): ParsedSelectedOption[] {
  const list = ContextualExpressions.getList(stripAnn(expr));
  if (!list) {
    return [];
  }
  return list.reduce<ParsedSelectedOption[]>((acc, item) => {
    const binding = ContextualExpressions.getSelect(stripAnn(item));
    if (binding) {
      acc.push({
        type: 'Bind',
        value: binding.prop,
        bindingExpr: stripAll(item),
      });
    } else {
      const itemUnion = ContextualExpressions.getUnion(stripAnn(item));
      if (itemUnion) {
        if (itemUnion.tag === 'Some') {
          const itemLiteral = ContextualExpressions.getLiteral(
            stripAnn(itemUnion.value)
          );
          const itemId = itemLiteral ? getString(itemLiteral.value) : undefined;
          if (itemId) {
            acc.push({type: 'Id', value: itemId});
          }
        }
      }
    }
    return acc;
  }, []);
}

function AutocompleteListBoxFooter({
  numSelectedItems,
  clearSelectedItems,
}: {
  numSelectedItems: number;
  clearSelectedItems: () => void;
}) {
  return (
    <HStack data-automation-id="cms-item-picker-footer" justify="space-between">
      <Text>{numSelectedItems} selected</Text>
      <Button
        variant="outline"
        isDisabled={numSelectedItems === 0}
        onClick={clearSelectedItems}
      >
        Deselect all
      </Button>
    </HStack>
  );
}

/**
 * Get the referenced collection type based on the `RefAnyOfConditionValue` type.
 */
function getReferencedCollectionType(type: Type): Type | null {
  const itemType = Types.getListItemType(type);
  const decon = itemType ? Types.deconstructTypeApplication(itemType) : null;
  if (decon && decon.args.length === 1) {
    return decon.args[0] ?? null;
  }
  return null;
}

/**
 * Get the referenced collection based on the `RefAnyOfConditionValue` type.
 */
function getReferencedCollection(
  type: Type,
  collections: CollectionStateType['collections']
): CollectionRecordType | null {
  const refCollectionType = getReferencedCollectionType(type);
  if (refCollectionType) {
    const record = Types.getRecordFields(refCollectionType);
    const collectionId = record ? Object.keys(record)[0] : null;
    return collectionId ? collections.get(collectionId) : null;
  }
  return null;
}

function selectCollectionsState(state: DesignerStores): CollectionStateType {
  return state.CollectionStore;
}

export function toListOfIds(value: RefConditionValue[]): string[] {
  return value.flatMap((item) => {
    if (item.tag === 'Some') {
      const maybeIdOrIds = item.val;
      if (typeof maybeIdOrIds === 'string') {
        return maybeIdOrIds;
      }
      if (
        Array.isArray(maybeIdOrIds) &&
        maybeIdOrIds.every((id) => typeof id === 'string')
      ) {
        return maybeIdOrIds;
      }
    }

    return emptyArray;
  });
}

const compareText = (inputValue: string, option: OptionType) => {
  if (!option.value.startsWith('DYN_CONTEXT')) {
    // non DYN_CONTEXT options are filtered in the backend
    return true;
  }
  return option.label.toLowerCase().includes(inputValue.toLowerCase());
};

const filterDynContextOptions: FilterOptionsType = ({options, targetValue}) => {
  if (!targetValue) {
    return options;
  }

  const filteredOptions: (OptionGroupType | OptionType)[] = [];

  for (const option of options) {
    if (isOptionGroup(option)) {
      const groupOptions = option.options.filter((groupOption) =>
        compareText(targetValue, groupOption)
      );

      if (groupOptions.length === option.options.length) {
        filteredOptions.push(option);
      } else {
        filteredOptions.push({...option, options: groupOptions});
      }
    } else if (compareText(targetValue, option)) {
      filteredOptions.push(option);
    }
  }

  return filteredOptions;
};

/**
 * Get fieldName and parentName from the DataType metadata of a DYN_CONTEXT
 * binding.
 */
function getDynContextBindingFieldAndParentName(
  value: string,
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>
): {fieldName: string | null; parentName: string | null} {
  const bindingType = getBindingType(
    envTypes,
    ESelect({
      from: EVariable(DYNAMO_VARIABLE),
      prop: value,
    })
  );
  const typeMeta = bindingType ? getMeta(bindingType) : null;
  const maybeParentName = typeMeta?.parentName;
  const maybeFieldName = typeMeta?.fieldName;
  return {
    fieldName:
      maybeFieldName && typeof maybeFieldName === 'string'
        ? maybeFieldName
        : null,
    parentName:
      maybeParentName && typeof maybeParentName === 'string'
        ? maybeParentName
        : null,
  };
}

function getCmsItemLabel(item: ItemRecordType): string {
  return item.get('name') || '';
}

export type RefAnyOfConditionLabel =
  | {type: 'binding'; text: string}
  | {type: 'text'; text: string};

/**
 * Compute display labels for the conditional preview. A binding whose
 * expression does not resolve to a backing type in `envTypes` is treated as
 * corrupt and rendered as plain "Unknown".
 */
export function getRefAnyOfConditionLabels({
  parsedSelectedOptions,
  selectedItems,
  envTypes,
}: {
  parsedSelectedOptions: ParsedSelectedOption[];
  selectedItems: ItemRecordType[];
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
}): RefAnyOfConditionLabel[] {
  return parsedSelectedOptions.map((option): RefAnyOfConditionLabel => {
    if (option.type === 'Bind') {
      const type = getBindingType(envTypes, option.bindingExpr);
      if (type) {
        const label = getBindingLabel(envTypes, option.bindingExpr);
        return {type: 'binding', text: label || 'Unknown'};
      }
      return {type: 'text', text: 'Unknown'};
    }
    const selectedItem = selectedItems.find(
      (cmsItem) => cmsItem && getId(cmsItem) === option.value
    );
    if (selectedItem) {
      return {type: 'text', text: getCmsItemLabel(selectedItem)};
    }
    return {type: 'text', text: 'Unknown'};
  });
}
